import { useCallback, useEffect } from 'react';

import Card from '@mui/material/Card';
import { Alert } from '@mui/material';
import Table from '@mui/material/Table';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getOverdueSettlements } from 'src/redux/slices/cheque-factoring';

import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import { IChequeItem } from 'src/types/cheque';

import OverdueTableRow from '../overdue-table-row';

const TABLE_HEAD = [
  { id: 'bank', label: 'Bank / Cheque Number', align: 'left', primary: true },
  { id: 'payer', label: 'Payer', align: 'left', primary: true },
  { id: 'amount', label: 'Amount', align: 'right', primary: true },
  { id: 'dueAt', label: 'Settlement Due', align: 'left', primary: true },
  { id: 'overdue', label: 'Overdue', align: 'left', primary: true },
  { id: '', primary: true },
];

export default function OverdueView() {
  const dispatch = useAppDispatcher();
  const { overdue } = useAppSelector((state) => state.chequeFactoring);

  const settings = useSettingsContext();
  const table = useTable({ defaultRowsPerPage: 25, defaultDense: true });
  const denseHeight = table.dense ? 60 : 80;

  const fetchOverdue = useCallback(() => {
    dispatch(
      getOverdueSettlements({
        page: table.page,
        limit: table.rowsPerPage,
        sortBy: 'factoringSettlementDueAt',
        sort: 'asc',
      })
    );
  }, [dispatch, table.page, table.rowsPerPage]);

  useEffect(() => {
    fetchOverdue();
  }, [fetchOverdue]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Overdue Settlements"
        links={[{ name: 'Cheque Factoring' }, { name: 'Overdue Settlements' }]}
        sx={{ mb: { xs: 3 } }}
      />

      {overdue.count > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {overdue.count} returned cheque(s) are past their 24-hour LOLC settlement window.
        </Alert>
      )}

      <Card>
        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom headLabel={TABLE_HEAD} rowCount={overdue.items.length} />

              <TableBody>
                {overdue.status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((_, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {overdue.items.map((row: IChequeItem) => (
                      <OverdueTableRow key={row._id} row={row} onSettled={fetchOverdue} />
                    ))}
                  </>
                )}

                {overdue.status !== 'failure' && overdue.count === 0 && (
                  <TableNoData
                    message="No overdue LOLC settlements — nothing urgent right now"
                    notFound={overdue.count === 0}
                  />
                )}

                {overdue.status === 'failure' && <TableErrorData notFound error={overdue.error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          dense
          count={overdue.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          onChangeDense={table.onChangeDense}
        />
      </Card>
    </Container>
  );
}
